// src/components/UserContractView/components/TemplatesSection.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  Avatar,
  Divider,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  FileCopy as CloneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  Launch as LaunchIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

const TemplatesSection = ({
  userTemplates,
  publicTemplates,
  onCreateTemplate,
  onEditTemplate,
  onCloneTemplate,
  onShareTemplate,
  onAssignTemplate,
  onDeleteTemplate,
  loading
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showPublic, setShowPublic] = useState(false);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <FolderIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">
                My Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userTemplates.length} custom {userTemplates.length === 1 ? 'template' : 'templates'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onCreateTemplate}
            >
              Create
            </Button>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {userTemplates.length === 0 ? (
            <Box textAlign="center" py={4}>
              <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Templates Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Create reusable contract templates for faster workflows
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>
                Create Your First Template
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {userTemplates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {template.name}
                        </Typography>
                        <Box display="flex" gap={0.5}>
                          {template.is_public ? (
                            <Tooltip title="Public template">
                              <PublicIcon fontSize="small" color="primary" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Private template">
                              <LockIcon fontSize="small" color="disabled" />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {template.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {template.description}
                        </Typography>
                      )}

                      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Chip
                          size="small"
                          label={template.contract_type === 'simple' ? 'Terms' : 'Contract'}
                          color={template.contract_type === 'simple' ? 'info' : 'primary'}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          v{template.version}
                        </Typography>
                      </Box>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEditTemplate(template)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Assign">
                          <IconButton size="small" color="primary" onClick={() => onAssignTemplate(template)}>
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={template.is_public ? 'Make private' : 'Make public'}>
                          <IconButton size="small" onClick={() => onShareTemplate(template)}>
                            {template.is_public ? <PublicIcon fontSize="small" /> : <ShareIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDeleteTemplate(template)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {publicTemplates.length > 0 && (
            <Box mt={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowPublic(!showPublic)}
                endIcon={showPublic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {showPublic ? 'Hide' : 'Show'} Available Templates ({publicTemplates.length})
              </Button>

              <Collapse in={showPublic}>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  {publicTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            {template.name}
                          </Typography>
                          {template.description && (
                            <Typography variant="body2" color="text.secondary">
                              {template.description}
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<CloneIcon />}
                            onClick={() => onCloneTemplate(template)}
                          >
                            Clone & Customize
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TemplatesSection;